import { ScenarioType } from 'context-system-model';
import { uniq } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { DCOCreativeList, fetchScenarios, ScenarioModalCreator, ScenarioSelector } from 'sonata-platform-components';
import {
  BoxV2,
  ContextualMenu,
  InputSearch,
  Toast,
  useQueryFetch,
  type ValidationError
} from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { FormExpandableSection } from '@/components/common/FormExpandableSection';
import DCOSkeleton from './DCOSkeleton/DCOSkeleton';
import { DynamicCreativeOptimizationStyled } from './DynamicCreativeOptimization.styled';
import { getDCOTags } from './helpers/getSectionHeaderTags.helper';

import type { Scenario } from 'context-system-model';
import type { DcoInterface, ScenariosInterface } from 'sonata-static-storage/lib/schemas/dco/dco.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

type ScenarioModalEditProps = React.ComponentProps<typeof DCOCreativeList>;

type Props = {
  // biome-ignore lint/suspicious/noExplicitAny: aligns with DCOCreativeList and SPC component contract
  ads: Record<string, any>[];
  campaign: ScenarioModalEditProps['campaign'];
  className?: string;
  countries: string[];
  dco: DcoInterface;
  error?: ValidationError;
  id?: string;
  onDcoChange: (dco: DcoInterface) => void;
  // biome-ignore lint/suspicious/noExplicitAny: aligns with DCOCreativeList and SPC component contract
  user: Record<string, any>;
  userRoles?: UserRoleInterface[];
};

type State = {
  defaultScenario?: Scenario;
  scenarioCreationIsOpened: boolean;
  scenarioSelectionIsOpened: boolean;
  scenarios: [];
  searchValue: string;
  userScenarios: Scenario[];
  userScenariosStatus: 'idle' | 'pending' | 'success' | 'error';
  showErrorToast: boolean;
};

const defaultState: State = {
  scenarioCreationIsOpened: false,
  scenarioSelectionIsOpened: false,
  scenarios: [],
  searchValue: '',
  showErrorToast: true,
  userScenarios: [],
  userScenariosStatus: 'idle'
};

const getDcoMaxPriority = (customScenarios: ScenariosInterface<string>[]) => {
  return customScenarios.length ? Math.max(...customScenarios.map(s => s.priority)) : 0;
};

const updateScenariosInDco = (dco: DcoInterface, newScenarios: Scenario[]) => {
  const customScenarios = dco.scenarios.filter(
    s => !s.defaultScenario && (!s.removed || newScenarios.find(nw => nw._id?.toString() !== s.scenario))
  );
  customScenarios.forEach(scenario => {
    if (scenario.priority === -1) {
      scenario.priority = getDcoMaxPriority(customScenarios) + 1;
    }
  });
  const dcoMaxPriority = getDcoMaxPriority(customScenarios);
  let newScenariosAddedCount = 0;
  return newScenarios
    .reduce((acc: ScenariosInterface<string>[], actualItem: Scenario) => {
      const currentScenarioInDco = dco.scenarios.find(s => s.scenario === actualItem._id);
      if (currentScenarioInDco) {
        return [...acc, currentScenarioInDco];
      }
      newScenariosAddedCount++;
      return [
        ...acc,
        {
          ads: [],
          defaultScenario: actualItem.type === ScenarioType.default,
          priority: actualItem.type === ScenarioType.default ? 100 : newScenariosAddedCount + dcoMaxPriority,
          scenario: actualItem._id!
        }
      ];
    }, [])
    .sort((scenario1, scenario2) => {
      if (scenario1.defaultScenario) {
        return -1;
      }
      if (scenario2.defaultScenario) {
        return 1;
      }
      return 0;
    });
};

const getSectionStatus = (DCOIsEnabled: boolean, areMinAds: boolean): 'available' | 'unavailable' => {
  return areMinAds && DCOIsEnabled ? 'available' : 'unavailable';
};

const fetchScenariosToken = {};

function DynamicCreativeOptimization({
  ads,
  campaign,
  className,
  countries,
  dco,
  error,
  id = 'dynamicCreativeOptimization-step3',
  onDcoChange,
  user,
  userRoles = []
}: Props) {
  const { formatMessage } = useIntl();
  const filteredScenarios = dco.scenarios?.filter(sce => !sce.removed) || [];
  const filteredScenariosIds = filteredScenarios.map(s => s.scenario.toString()) || [];
  const areMinAds = ads.length >= 2;
  const currentScenariosAds = uniq(
    filteredScenarios.reduce((acc: string[], actualItem: ScenariosInterface<string>) => {
      return [...acc, ...(actualItem.ads as string[])];
    }, [])
  );

  const [
    {
      defaultScenario,
      scenarioCreationIsOpened,
      scenarioSelectionIsOpened,
      searchValue,
      showErrorToast,
      userScenarios,
      userScenariosStatus
    },
    setState
  ] = useState<State>({
    ...defaultState
  });

  const hasPermissionsToCreate = checkUserResourcePermissions('contextBuilderCreateContext', user, userRoles);

  const updateState = (newState: Record<string, unknown>) => {
    setState(prevState => ({
      ...prevState,
      ...newState
    }));
  };

  const sectionStatus = getSectionStatus(!!dco.enabled, areMinAds);

  const disableDco = () => {
    const newDco = {
      ...dco,
      enabled: false
    };
    newDco.scenarios = campaign.isActive
      ? newDco.scenarios.map(sce => ({
          ...sce,
          ads: [],
          removed: !sce.defaultScenario
        }))
      : [];
    onDcoChange(newDco);
  };

  const hasDefaultScenario = dco.scenarios.some(s => s.defaultScenario);

  const scenariosData =
    dco.scenarios?.length > 0 && hasDefaultScenario
      ? { scenarioIds: dco.scenarios?.map(s => s.scenario) }
      : {
          type: ScenarioType.default,
          users: [user.id as string]
        };

  useQueryFetch(['scenarios', scenariosData, sectionStatus], async () => {
    if (sectionStatus === 'available' && userScenariosStatus !== 'success') {
      updateState({ userScenariosStatus: 'pending' });
      try {
        const data = await fetchScenarios(scenariosData, fetchScenariosToken);
        const newDco = { ...dco };
        newDco.enabled = true;
        const existingCustomScenarios = hasDefaultScenario
          ? data
          : [...data, ...userScenarios.filter(s => s.type !== ScenarioType.default)];
        newDco.scenarios = updateScenariosInDco(dco, existingCustomScenarios);
        onDcoChange(newDco);
        updateState({
          defaultScenario: data.find((e: Scenario) => e.type === ScenarioType.default),
          userScenarios: existingCustomScenarios,
          userScenariosStatus: 'success'
        });
      } catch (_e: unknown) {
        updateState({ userScenariosStatus: 'error' });
      }
    }
    if (sectionStatus === 'unavailable' && !!dco.enabled && userScenarios.length > 0) {
      updateState({ ...defaultState });
      disableDco();
    }
  });

  const handleScenarioSelectionToggled = (status: 'opened' | 'closed') => {
    updateState({ scenarioSelectionIsOpened: status === 'opened' });
  };
  const handleScenarioCreationToggled = (status: 'opened' | 'closed') => {
    updateState({ scenarioCreationIsOpened: status === 'opened' });
  };
  const handleScenarioSelectionSaveToggled = (scenarios: Scenario[]) => {
    const newScenarios = scenarios.map(scenario => userScenarios.find(s => s._id === scenario._id) || scenario);
    const newScenarioIds = newScenarios.map(s => s._id || '');
    const removedScenariosIds = dco.scenarios
      .filter(scenario => scenario.removed && !newScenarioIds.includes(scenario.scenario))
      .map(s => s.scenario);
    const removedScenarios = userScenarios.filter(scenario => removedScenariosIds.includes(scenario._id!));
    removedScenarios && newScenarios.unshift(...removedScenarios);
    defaultScenario && newScenarios.unshift(defaultScenario);
    const newDco = { ...dco };
    newDco.scenarios = updateScenariosInDco(dco, newScenarios);
    newDco.scenarios = newDco.scenarios.map(sce =>
      newScenarioIds.includes(sce.scenario)
        ? {
            ...sce,
            removed: false
          }
        : sce
    );
    newDco.enabled = true;
    onDcoChange(newDco);
    updateState({
      scenarioSelectionIsOpened: false,
      userScenarios: newScenarios
    });
  };

  const handleSearchInputChange = (value: string) => {
    setState(prevState => ({
      ...prevState,
      searchValue: value.trim().toLowerCase()
    }));
  };

  const handleDcoCreativesListChanged = (newScenariosDco: ScenariosInterface<string>[]) => {
    const newDco = { ...dco };
    newDco.scenarios = newScenariosDco;
    onDcoChange(newDco);
    const scenariosFormatted = newScenariosDco
      .map(nS => {
        if (!nS || !nS.scenario) {
          return undefined;
        }
        const found = userScenarios.find(uS => uS && uS._id === nS.scenario);
        return found || undefined;
      })
      .filter(Boolean) as Scenario[];
    updateState({ userScenarios: scenariosFormatted });
  };

  const handleScenarioCreationSaveToggled = (createdScenario: Scenario) => {
    const standardScenarios = userScenarios.filter(({ type }) => type === ScenarioType.standard);
    handleScenarioSelectionSaveToggled(standardScenarios.concat(createdScenario));
    updateState({ scenarioCreationIsOpened: false });
  };

  const handleScenariosChanged = (newUserScenarios: Scenario[]) => {
    const newDco = { ...dco };
    dco.scenarios = updateScenariosInDco(dco, newUserScenarios);
    onDcoChange(newDco);
    updateState({
      userScenarios: newUserScenarios
    });
  };

  const handleResetDCO = () => {
    disableDco();
    updateState({ ...defaultState });
  };

  const tooltipButton = () => {
    const buttons = [
      {
        disabled: !areMinAds,
        icon: '',
        id: `selectButton-${id}`,
        onClick: () => handleScenarioSelectionToggled('opened'),
        text: formatMessage({
          defaultMessage: 'Select Existing Context',
          id: 'dynamicCreativeOptimization.contexts.buttons.select'
        })
      }
    ];

    if (hasPermissionsToCreate) {
      buttons.push({
        disabled: !areMinAds,
        icon: '',
        id: `createButton-${id}`,
        onClick: () => handleScenarioCreationToggled('opened'),
        text: formatMessage({
          defaultMessage: 'Create context',
          id: 'dynamicCreativeOptimization.contexts.buttons.create'
        })
      });
    }

    return (
      <ContextualMenu
        buttonConfig={{
          disabled: !areMinAds,
          icon: 'fa-regular fa-plus',
          shape: 'squareRounded',
          size: 'medium',
          text: formatMessage({
            defaultMessage: 'Add contexts',
            id: 'dynamicCreativeOptimization.contexts.buttons.add'
          }),
          type: 'primary'
        }}
        buttons={buttons}
        direction="vertical"
        id={`addContext-menu-${id}`}
      />
    );
  };

  const showEmptyState = filteredScenarios.length === 0 || !areMinAds;
  const isSectionOpened = Boolean(dco.enabled);
  const tags = getDCOTags(dco);

  return (
    <DynamicCreativeOptimizationStyled className={`dynamicCreativeOptimization ${className ?? ''}`} id={id}>
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        hasErrors={Boolean(error?.message)}
        onReset={handleResetDCO}
        preffixId={id}
        tags={tags}
        title={formatMessage({
          defaultMessage: 'Dynamic Creative Optimization',
          id: 'dynamicCreativeOptimization.header.title'
        })}
      >
        <div>
          {error?.message && showErrorToast && (
            <Toast
              autoClose={false}
              className="settings__errorToast"
              closeToast={() => updateState({ showErrorToast: false })}
              icon="fal fa-exclamation-triangle"
              text={error?.message}
              type="danger"
            />
          )}
          {!areMinAds ? (
            <Toast
              autoClose={false}
              className="dynamicCreativeOptimization__toast"
              closeButton={false}
              icon="fal fa-exclamation-triangle"
              text={formatMessage({
                defaultMessage: 'To make the best use of the DCO, it is necessary to upload at least two creatives.',
                id: 'dynamicCreativeOptimization.toast.warning'
              })}
              type="warning"
            />
          ) : null}
          {showEmptyState ? (
            <BoxV2 className="dynamicCreativeOptimization__emptyState" id={'dynamicCreativeOptimization-boxCounter'}>
              <BoxV2.Body
                bgColor="dcoDefaultBackgroundColor"
                borderRadius="8px"
                className="dynamicCreativeOptimization__emptyStateContainer"
                padding="48px 16px"
              >
                <i className="fa-solid fa-empty-set dynamicCreativeOptimization__emptyStateIcon" />
                <div className="dynamicCreativeOptimization__emptyStateText">
                  <div className="dynamicCreativeOptimization__title">
                    {formatMessage({
                      defaultMessage: 'Start Adding your Contexts',
                      id: 'dynamicCreativeOptimization.emptyState.title'
                    })}
                  </div>
                  <span className="dynamicCreativeOptimization__description">
                    {formatMessage({
                      defaultMessage:
                        'Select or create a context and connect it to one or more creatives for targeting.',
                      id: 'dynamicCreativeOptimization.emptyState.description'
                    })}
                  </span>
                </div>
                {tooltipButton()}
              </BoxV2.Body>
            </BoxV2>
          ) : (
            <BoxV2 className="dynamicCreativeOptimization__content" id="dynamicCreativeOptimization-boxContent">
              <BoxV2.Body bgColor="multiFlightBackground02" borderRadius="8px" padding="16px">
                <div className="dynamicCreativeOptimization__contentAction">
                  <BoxV2
                    className="dynamicCreativeOptimization__contentCounterContainer"
                    id="dynamicCreativeOptimization-boxCounter"
                  >
                    <BoxV2.Body
                      bgColor="boxBgColor01"
                      borderRadius="8px"
                      className="dynamicCreativeOptimization__contentCounter"
                      padding="4px 16px"
                    >
                      <div className="dynamicCreativeOptimization__contentContexts">
                        <span className="dynamicCreativeOptimization__contentContextsText">
                          {formatMessage({
                            defaultMessage: 'Contexts',
                            id: 'dynamicCreativeOptimization.contexts.label'
                          })}
                        </span>
                        <span className="dynamicCreativeOptimization__contentContextsNumber">
                          {filteredScenarios.length}
                        </span>
                      </div>
                      <div className="dynamicCreativeOptimization__contentCreative">
                        <span className="dynamicCreativeOptimization__contentCreativeText">
                          {formatMessage({
                            defaultMessage: 'Creatives applied',
                            id: 'dynamicCreativeOptimization.creatives.label'
                          })}
                        </span>
                        <span className="dynamicCreativeOptimization__contentCreativeNumber">
                          {currentScenariosAds.length}
                        </span>
                      </div>
                    </BoxV2.Body>
                  </BoxV2>
                  <InputSearch
                    className="dynamicCreativeOptimization__contentSearchInput"
                    id={`searchInput-${id}`}
                    lens
                    name="search"
                    onChange={handleSearchInputChange}
                    placeholder={formatMessage({
                      defaultMessage: 'Search by name...',
                      id: 'dynamicCreativeOptimization.inputSearch.placeholder'
                    })}
                  />
                  <div className="dynamicCreativeOptimization__contentActionSection">{tooltipButton()}</div>
                </div>
                {userScenariosStatus === 'pending' ? (
                  <DCOSkeleton />
                ) : (
                  <DCOCreativeList
                    DCOScenarios={dco?.scenarios}
                    campaign={campaign}
                    campaignType={campaign.type}
                    creatives={ads}
                    devicePlatforms={campaign.devicePlatforms}
                    onChange={handleDcoCreativesListChanged}
                    onScenariosChange={handleScenariosChanged}
                    scenarios={userScenarios}
                    searchValue={searchValue}
                    user={user}
                    userRoles={userRoles}
                  />
                )}
              </BoxV2.Body>
            </BoxV2>
          )}
          <ScenarioSelector
            campaignId={campaign._id}
            campaignType={campaign.type}
            country={countries.length > 1 ? 'allCountries' : countries[0]}
            devicePlatforms={campaign.devicePlatforms}
            isModalOpened={scenarioSelectionIsOpened}
            onModalClose={() => handleScenarioSelectionToggled('closed')}
            onModalSave={handleScenarioSelectionSaveToggled}
            selectedScenariosIds={filteredScenariosIds}
            user={user as unknown as UserInterface<string>}
            userRoles={userRoles}
          />
          <ScenarioModalCreator
            campaignId={campaign._id}
            campaignType={campaign.type}
            country={countries.length > 1 ? 'allCountries' : countries[0]}
            devicePlatforms={campaign.devicePlatforms}
            isModalOpened={scenarioCreationIsOpened}
            onModalClose={() => handleScenarioCreationToggled('closed')}
            onModalSave={handleScenarioCreationSaveToggled}
            user={user as unknown as UserInterface<string>}
            userRoles={userRoles}
          />
        </div>
      </FormExpandableSection>
    </DynamicCreativeOptimizationStyled>
  );
}

export default DynamicCreativeOptimization;
